export const DEFAULT_EARLY_CLOSE_SEGMENTS = [
  { segment: 1, min: 0, max: 25, win_percentage: 16, loss_percentage: -33, percentage: 16 },
  { segment: 2, min: 25, max: 50, win_percentage: 40, loss_percentage: -58, percentage: 40 },
  { segment: 3, min: 50, max: 75, win_percentage: 64, loss_percentage: -83, percentage: 64 },
  { segment: 4, min: 75, max: 100, win_percentage: 88, loss_percentage: -96, percentage: 88 },
];

export const parseDurationSeconds = (val) => {
  if (!val) return 0;
  if (typeof val === "number") return val;
  const str = String(val).trim().toLowerCase();
  if (/^\d+(\.\d+)?$/.test(str)) return Number(str);
  if (str.endsWith("m")) return (parseFloat(str) || 0) * 60;
  if (str.endsWith("s")) return parseFloat(str) || 0;
  if (str.endsWith("h")) return (parseFloat(str) || 0) * 3600;
  const num = parseFloat(str);
  return Number.isFinite(num) ? num : 0;
};

export const getOrderTimeDetails = (order, stickyEnd, nowMs) => {
  const parseMs = (val) => {
    if (!val) return 0;
    if (typeof val === "number") return val < 1e11 ? val * 1000 : val;
    const d = new Date(val).getTime();
    if (!isNaN(d) && d > 0) return d;
    const num = Number(val);
    if (!isNaN(num) && num > 0) return num < 1e11 ? num * 1000 : num;
    return 0;
  };

  const parsedDuration =
    parseDurationSeconds(order?.durationSeconds) ||
    parseDurationSeconds(order?.duration) ||
    0;

  let endMs =
    stickyEnd ||
    parseMs(order?.expiresAtMs) ||
    parseMs(order?.endTime) ||
    parseMs(order?.expiryTime) ||
    0;

  let startMs =
    parseMs(order?.createdAtMs) ||
    parseMs(order?.startTime) ||
    parseMs(order?.entryTime) ||
    parseMs(order?.createdAt) ||
    0;

  if (!startMs && endMs && parsedDuration > 0) {
    startMs = endMs - parsedDuration * 1000;
  }
  if (!endMs && startMs && parsedDuration > 0) {
    endMs = startMs + parsedDuration * 1000;
  }

  const currentNow = nowMs || Date.now();
  if (!endMs) {
    endMs = currentNow + (parsedDuration > 0 ? parsedDuration : 30) * 1000;
  }
  if (!startMs) {
    startMs = endMs - (parsedDuration > 0 ? parsedDuration : 30) * 1000;
  }

  const totalDurationMs = Math.max(1000, endMs - startMs);
  const durationSeconds = parsedDuration > 0 ? parsedDuration : Math.max(1, Math.round(totalDurationMs / 1000));
  const remainingMs = Math.max(0, endMs - currentNow);
  const remainingSeconds = Math.ceil(remainingMs / 1000);
  const elapsedMs = Math.max(0, Math.min(totalDurationMs, totalDurationMs - remainingMs));
  const progressPercentage = Math.min(100, Math.max(0, (elapsedMs / totalDurationMs) * 100));

  return {
    startMs,
    endMs,
    totalDurationMs,
    durationSeconds,
    remainingSeconds,
    remainingMs,
    progressPercentage: Number(progressPercentage.toFixed(2)),
  };
};

export const normalizeProfitDetails = (profitDetails, currentRank = "basic") => {
  let list = [];
  if (Array.isArray(profitDetails) && profitDetails.length > 0) {
    list = profitDetails;
  } else if (profitDetails && typeof profitDetails === "object") {
    const rankKey = String(currentRank || "basic").toLowerCase();
    list =
      profitDetails[rankKey] ||
      profitDetails.basic ||
      Object.values(profitDetails)[0] ||
      [];
  }

  if (!Array.isArray(list) || list.length === 0) {
    return DEFAULT_EARLY_CLOSE_SEGMENTS;
  }

  return list.map((item, idx) => {
    const segNum = Number(item.segment) || idx + 1;
    const def =
      DEFAULT_EARLY_CLOSE_SEGMENTS.find((d) => d.segment === segNum) ||
      DEFAULT_EARLY_CLOSE_SEGMENTS[idx] ||
      {};
    const winVal = item.win_percentage !== undefined ? Number(item.win_percentage) : (Number(item.percentage) || def.win_percentage || 16);
    let lossVal = def.loss_percentage;
    if (item.loss_percentage !== undefined && item.loss_percentage !== null) {
      const parsed = Number(item.loss_percentage);
      lossVal = parsed > 0 ? -parsed : parsed;
    } else if (item.percentage !== undefined) {
      lossVal = -Math.abs(Number(item.percentage));
    }
    return {
      segment: segNum,
      min: item.min !== undefined ? Number(item.min) : (def.min ?? (segNum - 1) * 25),
      max: item.max !== undefined ? Number(item.max) : (def.max ?? segNum * 25),
      win_percentage: winVal,
      loss_percentage: lossVal,
      percentage: winVal,
    };
  });
};

export const getProgressPercentage = ({ startTime, endTime, closedTime }) => {
  const parseTimeMs = (val) => {
    if (!val) return NaN;
    if (typeof val === "number") {
      return val < 1e11 ? val * 1000 : val;
    }
    const d = new Date(val).getTime();
    if (!isNaN(d)) return d;
    const n = Number(val);
    if (!isNaN(n)) return n < 1e11 ? n * 1000 : n;
    return NaN;
  };

  const start = parseTimeMs(startTime);
  const end = parseTimeMs(endTime);
  const closed = parseTimeMs(closedTime) || Date.now();

  const totalDuration = end - start;
  const elapsedDuration = closed - start;

  if (!Number.isFinite(totalDuration) || totalDuration <= 0) return 0;

  let progressPercent = (elapsedDuration / totalDuration) * 100;
  progressPercent = Math.max(0, Math.min(progressPercent, 100));

  return Number(progressPercent.toFixed(1));
};

export const getProfitPercentage = (
  progressPercentage,
  profitDetails,
  defaultWinPercentage = 96,
  userRank = "basic",
  isWinning = true,
) => {
  const segments = normalizeProfitDetails(profitDetails, userRank);
  const p = Math.max(0, Math.min(Number(progressPercentage) || 0, 100));

  const sorted = [...segments].sort(
    (a, b) => (Number(a.segment) || 0) - (Number(b.segment) || 0),
  );

  const matched = sorted.find((item) => {
    const min = item.min !== undefined ? Number(item.min) : ((Number(item.segment) - 1) * 25);
    const max = item.max !== undefined ? Number(item.max) : (Number(item.segment) * 25);
    return p >= min && p <= max;
  });

  const seg = matched || sorted[sorted.length - 1];
  if (!seg) {
    return isWinning ? (Number(defaultWinPercentage) || 88) : -96;
  }

  if (isWinning) {
    return seg.win_percentage !== undefined ? Number(seg.win_percentage) : (Number(seg.percentage) || 16);
  } else {
    if (seg.loss_percentage !== undefined) {
      const parsed = Number(seg.loss_percentage);
      return parsed > 0 ? -parsed : parsed;
    }
    return -Math.abs(Number(seg.percentage) || 33);
  }
};

